import { AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Alert, Box, Tag, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import { Element } from 'react-scroll';

import type { FormSubmitHandler, SmartContractMethod } from './types';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import { useI18n } from 'lib/i18n/context';
import CopyToClipboard from 'ui/shared/CopyToClipboard';
import Hint from 'ui/shared/Hint';

import { ADDRESS_CONTRACT_KEYS } from '../../i18n';
import { useContractMethodFieldStyles } from './form/contractMethodFieldStyles';
import ContractMethodForm from './form/ContractMethodForm';
import { getElementId, getElementName } from './useScrollToMethod';
import { isReadMethod } from './utils';

interface Props {
  data: SmartContractMethod;
  index: number;
  id: number;
  addressHash: string;
  sourceAddress?: string;
  tab: string;
  onSubmit: FormSubmitHandler;
  isVisible?: boolean;
}

const ContractAbiItem = ({ data, index, id, addressHash, sourceAddress, tab, onSubmit, isVisible = true }: Props) => {
  const { t } = useI18n();
  const [ attempt, setAttempt ] = React.useState(0);
  const fieldStyles = useContractMethodFieldStyles();
  const methodIdBg = useColorModeValue('#F3F4F6', '#212121');
  const methodIdBorder = useColorModeValue('#E9ECEF', '#222222');
  const methodIdColor = useColorModeValue('#616161', '#999999');
  const readTagBg = useColorModeValue('#EFE3FF', '#1a365d');
  const readTagColor = useColorModeValue('#7B43B4', '#90cef4');
  const writeTagBg = useColorModeValue('#E7F1F8', '#1a365d');
  const writeTagColor = useColorModeValue('#356E95', '#90cef4');
  const titleColor = useColorModeValue('#1F2328', '#FFFFFF');
  const iconColor = useColorModeValue('#A0AEC0', '#999999');

  const url = React.useMemo(() => {
    return config.app.baseUrl + route({
      pathname: '/address/[hash]',
      query: {
        hash: addressHash ?? '',
        tab,
        ...(sourceAddress ? { source_address: sourceAddress } : {}),
      },
      hash: getElementId(data),
    });
  }, [ addressHash, data, tab, sourceAddress ]);

  const handleReset = React.useCallback(() => {
    setAttempt((prev) => prev + 1);
  }, []);

  const isRead = isReadMethod(data);

  return (
    <AccordionItem
      as="section"
      border="none"
      display={ isVisible ? 'block' : 'none' }
      mb={ 2 }
    >
      { ({ isExpanded }) => (
        <Box
          bg={ fieldStyles.methodItemBg }
          borderRadius="8px"
          p={ 4 }
        >
          <Element as="h2" name={ getElementName(data) }>
            <AccordionButton
              px={ 0 }
              py={ 0 }
              _hover={{ bgColor: 'transparent' }}
              wordBreak="break-all"
              textAlign="left"
              as="div"
              cursor="pointer"
              display="flex"
              alignItems="center"
              columnGap={ 2 }
              color={ titleColor }
            >
              <CopyToClipboard text={ url } type="link" ml={ 0 } color="text_secondary"/>
              <Box as="div" fontWeight={ 500 } fontSize="14px" display="flex" alignItems="center">
                { index + 1 }. { data.type === 'fallback' || data.type === 'receive' ? data.type : data.name }
                { data.type === 'fallback' && (
                  <Hint
                    label={ t(ADDRESS_CONTRACT_KEYS.notice.fallbackHint) }
                    ml={ 1 }
                  />
                ) }
                { data.type === 'receive' && (
                  <Hint
                    label={ t(ADDRESS_CONTRACT_KEYS.notice.receiveHint) }
                    ml={ 1 }
                  />
                ) }
              </Box>
              <Tag
                flexShrink={ 0 }
                bg={ isRead ? readTagBg : writeTagBg }
                color={ isRead ? readTagColor : writeTagColor }
                borderRadius="4px"
                textTransform="lowercase"
                fontWeight={ 500 }
                fontSize="12px"
                h="24px"
                px={ 2 }
              >
                { t(isRead ? ADDRESS_CONTRACT_KEYS.actions.read : ADDRESS_CONTRACT_KEYS.actions.write) }
              </Tag>
              { 'method_id' in data && (
                <Tag
                  display="inline-flex"
                  alignItems="center"
                  flexShrink={ 0 }
                  bg={ methodIdBg }
                  color={ methodIdColor }
                  border="1px solid"
                  borderColor={ methodIdBorder }
                  borderRadius="4px"
                  fontSize="12px"
                  h="24px"
                  px={ 2 }
                  gap={ 1 }
                >
                  { data.method_id }
                  <CopyToClipboard text={ data.method_id } size={ 4 }/>
                </Tag>
              ) }
              <AccordionIcon
                ml="auto"
                transform={ isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }
                color={ iconColor }
              />
            </AccordionButton>
          </Element>
          <AccordionPanel p={ 0 } pt={ 4 }>
            { 'is_invalid' in data && data.is_invalid ? (
              <Alert status="warning">{ t(ADDRESS_CONTRACT_KEYS.notice.methodParseError) }</Alert>
            ) : (
              <ContractMethodForm
                key={ id + '_' + index + '_' + attempt }
                data={ data }
                attempt={ attempt }
                onSubmit={ onSubmit }
                onReset={ handleReset }
                isOpen={ isExpanded }
              />
            ) }
          </AccordionPanel>
        </Box>
      ) }
    </AccordionItem>
  );
};

export default React.memo(ContractAbiItem);
